import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useDataSource } from '@afosto/app-components-react/DataSource';
import { FormattedMessage } from 'react-intl';
import AppBar from '@material-ui/core/AppBar';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import PutawayHistoryItem from './PutawayHistoryItem';
import ProcessHandlingListContext from '../../../../components/ProcessHandlingListContext';
import DataList from '@afosto/app-components-react/DataList';
import Dialog from '@afosto/app-components-react/Dialog';
import Icon from '@afosto/app-components-react/Icon';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import { search as searchApi } from '@afosto/fulfilment/api/Search';
import translations from './translations';

const DataListContainerGrid = props => <Grid container spacing={1} {...props} />;

const PutawayHistoryDialog = ({ isOpen, onClose }) => {
  const { handlingList } = useContext(ProcessHandlingListContext);
  const handlingListId = handlingList?.id;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const fetchHistoryItems = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (data, requestOptions) =>
        searchApi.searchHandlingListItems(
          {
            constraints: [
              {
                key: 'list_ids',
                operator: 'in',
                value: [handlingListId],
              },
              {
                key: 'is_handled',
                operator: 'in',
                value: true,
              },
            ],
          },
          requestOptions,
        ),
      {
        transformData: async (data, source) => {
          const { pagination = {} } = source ?? {};
          const { itemsPerPage } = pagination;
          const skus = data.map(({ sku }) => sku);
          const products = await fetchMultipleProducts(
            skus,
            {},
            {
              headers: {
                'x-page-size': itemsPerPage,
              },
            },
          );
          const mappedProducts = products.reduce(
            (acc, product) => ({ ...acc, [product?.sku]: product }),
            {},
          );

          return data.reduce(
            (acc, item) => [...acc, { ...item, product: mappedProducts[item.sku] }],
            [],
          );
        },
      },
    );

  const dataSource = useDataSource(fetchHistoryItems, {
    modules: ['pagination'],
    pagination: {
      itemsPerPage: 10,
    },
  });
  const dataSourceRef = useRef(dataSource);

  useEffect(() => {
    dataSourceRef.current = dataSource;
  });

  useEffect(() => {
    if (isOpen) {
      dataSourceRef.current.reloadDataSource();
    }
  }, [isOpen]);

  const renderHistoryItem = item => <PutawayHistoryItem key={item?.id} item={item} />;

  return (
    <Dialog open={isOpen} fullScreen={isMobile} onClose={onClose} fullWidth>
      <AppBar position="sticky" color="default">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <Icon icon={['fal', 'times']} />
          </IconButton>
          <Typography variant="h6">
            <FormattedMessage {...translations.title} />
          </Typography>
        </Toolbar>
      </AppBar>
      <Box p={2}>
        <DataList
          component={DataListContainerGrid}
          dataSource={dataSource}
          renderItem={renderHistoryItem}
        />
      </Box>
    </Dialog>
  );
};

PutawayHistoryDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

PutawayHistoryDialog.defaultProps = {
  isOpen: false,
  onClose: undefined,
};

export default PutawayHistoryDialog;
